import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDownLeft, RefreshCw, ScanLine } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import { addressFor, fmtNum, fmtUsd, shorten, getChain } from "@/lib/chains";
import { fetchAssetBalance } from "@/lib/rpc";
import { buildAssets } from "@/lib/tokens";
import { api } from "@/lib/api";
import ChainIcon from "@/components/ChainIcon";

const STABLE_COLOR = { USDT: "#26A17B", USDC: "#2775CA" };

export default function Portfolio() {
  const nav = useNavigate();
  const { accounts } = useWallet();
  const assets = useMemo(() => buildAssets(), []);
  const [prices, setPrices] = useState({});
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: pr }, balResults] = await Promise.all([
        api.get("/prices"),
        Promise.all(assets.map((a) => fetchAssetBalance(a, accounts).then((b) => [a.id, b]))),
      ]);
      setPrices(pr || {});
      setBalances(Object.fromEntries(balResults));
    } catch (e) {
      // keep previous
    } finally {
      setLoading(false);
    }
  }, [accounts, assets]);

  useEffect(() => { load(); }, [load]);

  const enrich = (a) => {
    const bal = balances[a.id] || 0;
    const price = prices[a.priceKey]?.price || 0;
    const change = prices[a.priceKey]?.change_24h || 0;
    return { a, bal, price, change, usd: bal * price };
  };
  const nativeRows = assets.filter((a) => a.kind === "native").map(enrich);
  const tokenRows = assets.filter((a) => a.kind === "token" && (balances[a.id] || 0) > 0).map(enrich);
  const rows = [...nativeRows, ...tokenRows];
  const total = rows.reduce((s, r) => s + r.usd, 0);
  const totalPrev = rows.reduce((s, r) => s + r.usd / (1 + (r.change || 0) / 100), 0);
  const totalChangePct = totalPrev > 0 ? ((total - totalPrev) / totalPrev) * 100 : 0;

  return (
    <div className="px-5 pt-6">
      {/* header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="font-sans text-xs text-[#6B7280]">Total balance</p>
          <p className="font-outfit text-sm font-semibold text-white">Main Vault</p>
        </div>
        <button data-testid="refresh-portfolio-button" onClick={load} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1F2430] text-[#9CA3AF] hover:text-neon">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* total */}
      <div className="card-surface p-6 fade-up">
        <p data-testid="total-balance-display" className="font-rajdhani text-5xl font-bold text-white">
          {fmtUsd(total)}
        </p>
        <div className="mt-2 flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 font-rajdhani text-sm font-semibold ${totalChangePct >= 0 ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
            {totalChangePct >= 0 ? "+" : ""}{fmtNum(totalChangePct, 2)}%
          </span>
          <span className="font-sans text-xs text-[#6B7280]">24h</span>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <QuickAction testid="quick-send-button" icon={ArrowUpRight} label="Send" onClick={() => nav("/send")} primary />
          <QuickAction testid="quick-receive-button" icon={ArrowDownLeft} label="Receive" onClick={() => nav("/receive")} />
          <QuickAction testid="quick-scan-button" icon={ScanLine} label="Scan" onClick={() => nav("/send?scan=1")} />
        </div>
      </div>

      {/* assets */}
      <p className="mb-3 mt-7 font-outfit text-sm font-semibold text-[#9CA3AF]">Assets</p>
      <div className="space-y-2.5">
        {rows.map(({ a, bal, price, change, usd }) => {
          const chain = getChain(a.chain);
          const isToken = a.kind === "token";
          return (
            <div
              key={a.id}
              data-testid={isToken ? "token-balance-card" : "chain-balance-card"}
              onClick={() => nav(`/receive?chain=${a.chain}`)}
              className="flex cursor-pointer items-center gap-3 card-surface p-4 transition-colors hover:border-neon/30"
            >
              {isToken ? (
                <div className="relative">
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-full font-rajdhani text-[11px] font-bold text-white"
                    style={{ background: `${STABLE_COLOR[a.symbol] || "#26A17B"}` }}
                  >
                    {a.symbol}
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-2 border-[#12141A]" style={{ background: chain.color }} />
                </div>
              ) : (
                <ChainIcon chain={chain} size={44} />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-outfit text-sm font-semibold text-white">{isToken ? a.symbol : chain.name}</p>
                  <span className={`font-rajdhani text-xs ${change >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                    {change >= 0 ? "+" : ""}{fmtNum(change, 1)}%
                  </span>
                </div>
                <p className="font-sans text-xs text-[#6B7280]">
                  {isToken ? `on ${chain.name}` : shorten(addressFor(accounts, a.chain))} · {fmtUsd(price)}
                </p>
              </div>
              <div className="text-right">
                <p className="font-rajdhani text-base font-semibold text-white">{fmtNum(bal)} {a.symbol}</p>
                <p className="font-rajdhani text-xs text-[#9CA3AF]">{fmtUsd(usd)}</p>
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-center font-sans text-xs text-[#6B7280]">
        USDT &amp; USDC supported on Ethereum, BSC, Polygon, Tron &amp; Solana
      </p>
      <div className="h-4" />
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, primary, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex flex-col items-center gap-1.5 rounded-2xl py-3 transition-transform active:scale-95 ${
        primary ? "bg-neon text-[#08090C] neon-glow" : "border border-[#1F2430] bg-[#12141A] text-white hover:border-neon/40"
      }`}
    >
      <Icon className="h-5 w-5" />
      <span className="font-outfit text-xs font-semibold">{label}</span>
    </button>
  );
}
